import React, { useEffect, useMemo, useState } from 'react';
import { 
  Bullseye, 
  Button, 
  Card, 
  CardBody, 
  CardFooter,
  CardHeader,
  CardTitle,
  EmptyState,
  EmptyStateBody,
  Icon,
  Pagination,
  PaginationVariant,
  Spinner,
  Tooltip
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@patternfly/react-icons';
import { fetchData } from '@app/utils/fetchData';
import { ddMMyyyyHHmmTzFormat } from '@app/utils/functions';
import { Klass, instructorNames } from '@app/model/Klass';
import { KlassFlagSummary } from '@app/components/KlassFlagSummary';

export const ClassesAtRiskCard: React.FC = () => {
  // --- Data & Loading State ---
  const [classesAtRisk, setClassesAtRisk] = useState<Klass[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasHitLimit, setHasHitLimit] = useState<boolean>(false);

  // --- Client-Side Pagination State ---
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(5);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch up to 50 results exactly once on mount
        const response = await fetchData(`/dashboard/classesatrisk?pageSize=50`);
        
        if (isMounted) {
          const items = Array.isArray(response) ? response : response.content || response.items || [];
          setClassesAtRisk(items);
          
          // Trigger the warning if the API returns exactly 50 items
          setHasHitLimit(items.length === 50);
        }
      } catch (error) {
        console.error("Failed to load classes at risk:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array: Only fetch once when the component mounts

  const paginatedClasses = useMemo(() => {
    const startIndex = (page - 1) * perPage;
    return classesAtRisk.slice(startIndex, startIndex + perPage);
  }, [classesAtRisk, page, perPage]);

  return (
    <Card isFullHeight>
      <CardHeader
        actions={{
          actions: (
            <Button variant="link" isInline onClick={() => console.log('View all at risk')}>
              View all
            </Button>
          )
        }}
      >
        <CardTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            Classes at Risk
            {hasHitLimit && (
              <Tooltip content="Displaying the maximum of 50 flagged classes. Please use the 'View all' link to see the complete list.">
                <Icon status="warning" size="sm" style={{ cursor: 'help' }}>
                  <ExclamationTriangleIcon />
                </Icon>
              </Tooltip>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardBody style={{ padding: 0 }}>
        {isLoading ? (
          <Bullseye style={{ minHeight: '150px' }}>
            <Spinner size="lg" aria-label="Loading classes at risk" />
          </Bullseye>
        ) : classesAtRisk.length === 0 ? (
          <EmptyState variant="sm" titleText="All clear!" icon={CheckCircleIcon}>
            <EmptyStateBody>No classes currently have critical flags.</EmptyStateBody>
          </EmptyState>
        ) : (
          <Table aria-label="Classes at Risk Table" variant="compact" borders={false}>
            <Thead>
              <Tr>
                <Th>SKU</Th>
                <Th>Start Date</Th>
                <Th>Allocated Instructor</Th>
                <Th>Total Students</Th>
                <Th>Language</Th>
                <Th>Flags</Th>
              </Tr>
            </Thead>
            <Tbody>
              {/* Render the sliced array instead of the full array */}
              {paginatedClasses.map((klass) => (
                <Tr key={klass.id}>
                  <Td dataLabel="SKU"><strong>{klass.course?.sku}</strong></Td>
                  <Td dataLabel="Date">{ddMMyyyyHHmmTzFormat(klass.startDateTime)}</Td>
                  <Td dataLabel="Instructor">{instructorNames(klass.allocatedInstructors) || 'n/a'}</Td>
                  <Td dataLabel="Total Students">{klass.totalEnrolled}</Td>
                  <Td dataLabel="Language">{klass.language?.englishName}</Td>
                  <Td dataLabel="Flags">
                    <KlassFlagSummary flags={klass.activeFlags || []} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </CardBody>
      
      {/* Pagination Controls */}
      {classesAtRisk.length > 0 && (
        <CardFooter style={{ padding: '0 16px 16px 16px' }}>
          <Pagination
            isCompact
            itemCount={classesAtRisk.length}
            page={page}
            perPage={perPage}
            onSetPage={(_evt, newPage) => setPage(newPage)}
            onPerPageSelect={(_evt, newPerPage, newPage) => {
              setPerPage(newPerPage);
              setPage(newPage);
            }}
            variant={PaginationVariant.bottom}
          />
        </CardFooter>
      )}
    </Card>
  );
};